import cn from 'classnames';
import { useEffect, useState } from 'react';
import Calendar, { TileClassNameFunc } from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { BookingParams } from '.';

type Props = {
  updateStepState: (param: string) => void;
  updateBookingParams: (params: BookingParams) => void;
  nextStep: string;
  availableOnDates: string[];
  bookingParams: BookingParams;
};

const yesterday = new Date();
yesterday.setDate(yesterday.getDate() - 1);
yesterday.setHours(23, 59, 50);

const DateSelectionForm = ({
  updateStepState,
  updateBookingParams,
  nextStep,
  availableOnDates,
  bookingParams,
}: Props) => {
  const [defaultValue, setDefaultValue] = useState<Date | undefined>(undefined);

  useEffect(() => {
    if (bookingParams.selectedDateTime) {
      setDefaultValue(new Date(bookingParams.selectedDateTime));
    }
  }, [bookingParams.selectedDateTime]);

  const firstAvailableDate = new Date(availableOnDates[0]);

  function tileDisabled({ date }: { date: Date }) {
    return (
      isDateLessThanToday(date) || blockDatesTooFarInTheFuture(date) || !isAvailableOnDate(date)
    );
  }

  const isDateLessThanToday = (date: Date) => {
    return yesterday.getTime() > date.getTime();
  };

  const blockDatesTooFarInTheFuture = (date: Date) => {
    const BLOCK_DAYS_IN_ADVANCE = 90;
    const currentDate = new Date();
    currentDate.setDate(currentDate.getDate() + BLOCK_DAYS_IN_ADVANCE);

    return currentDate.getTime() <= date.getTime();
  };

  const isAvailableOnDate = (date: Date) => {
    const localDates = availableOnDates.map((date) => {
      const dateObj = new Date(date);
      dateObj.setHours(0, 0, 0, 0);

      return dateObj.toISOString();
    });

    return localDates.includes(date.toISOString());
  };

  const areDatesSameDay = (date1: Date, date2: Date) => {
    return date1.toDateString() === date2.toDateString();
  };

  const renderTitleContent: TileClassNameFunc = ({ date }) => {
    const isDisabled = tileDisabled({ date });
    const isActive =
      bookingParams?.selectedDateTime && areDatesSameDay(bookingParams.selectedDateTime, date);
    return cn(
      '!rounded-full text-lg calendar-tile',
      { '!text-primary-default calendar-tile !bg-transparent active  ': !isActive && !isDisabled },
      { '!bg-primary-default !text-primary-contrast': isActive },
      { '!line-through !text-gray-400': isDisabled }
    );
  };

  return (
    <div className={'w-full flex justify-center gap-x-2 '}>
      <Calendar
        defaultValue={defaultValue ?? firstAvailableDate}
        tileDisabled={tileDisabled}
        tileClassName={renderTitleContent}
        onClickDay={(selectedDateTime) => {
          updateBookingParams({
            ...bookingParams,
            selectedDateTime,
          });
          updateStepState(nextStep);
        }}
      />
    </div>
  );
};

export default DateSelectionForm;
